// Updated RegistrationPage.tsx to match SignInPage UI
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import firebase from "firebase";
import queryString from "query-string";
import { AxiosError } from "axios";

import TextfieldDefault from "../../../../components/textField/TextfieldDefault";
import ValidationMessage from "../../../../components/validation/ValidationMessage";
import {
  getCustomToken,
  getCustomerValidationNew,
  customerUpdateNew,
} from "../../../../services/accountApp/authService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) => ({
  root: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    width: "200%",
    height: 600, // 🔼 Deep curve
    borderBottomLeftRadius: "100%",
    borderBottomRightRadius: "100%",
    position: "absolute",
    top: -380, // 🔼 Shift it upward to center the curve
    left: "-50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingBottom: 24, // Optional space below logo
  },

  headerWrapper: {
    paddingTop: 220, // 🔼 Matches new curve depth
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  content: {
    flexGrow: 1,
    padding: "20px 20px",
  },
  registerWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  bottomText: {
    textAlign: "center",
    fontSize: 12,
    marginTop: 20,
    padding: "0 20px",
  },
  logo: {
    width: 160,
    height: "auto",
    marginBottom: 8,
    paddingTop: "70px",
  },
}));

interface ManageAppsProps {
  handleChangeLoginStatus: () => void;
  isLoginOrRegister: boolean;
  setAuthError: (msg: string) => void;
}

interface CustomerForm {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const RegistrationPage: React.FC<ManageAppsProps> = ({
  handleChangeLoginStatus,
  isLoginOrRegister,
  setAuthError,
}) => {
  const classes = useStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState<any>({
    headColour: "#f57c00",
    backColour: "#ffffff",
    buttonColour: "#000000",
    headText: "#ffffff",
    backText: "#000000",
    ButtonText: "#ffffff",
    title: "Register",
    SubTitle: "",
    logo: "",
  });

  const { register, handleSubmit, errors, trigger, getValues } =
    useForm<CustomerForm>();

  useEffect(() => {
    const parsed = queryString.parse(window.location.search);
    const colorKeys = [
      "headColour",
      "backColour",
      "buttonColour",
      "headText",
      "backText",
      "ButtonText",
      "title",
      "SubTitle",
      "logo",
    ];
    const updatedParams: any = { ...parsed };
    colorKeys.forEach((key) => {
      if (updatedParams[key] && typeof updatedParams[key] === "string") {
        const val = updatedParams[key] as string;
        updatedParams[key] =
          key === "title" || key === "SubTitle" || key === "logo"
            ? val
            : `#${val}`;
      }
    });
    setParams((prev: any) => ({
      ...prev,
      ...updatedParams,
    }));
  }, []);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((prev) => !prev);

  const handleCreateCustomToken = async (idToken: string) => {
    try {
      const res = await getCustomToken({ idToken });
      if (res?.status === 200) {
        const redirectUri = queryString.parse(
          window.location.search,
        ).redirectUri;
        const queryParams = `t=${res.data.customToken}`;
        setTimeout(() => {
          window.location.href = `${redirectUri}?${queryParams}`;
        }, 500);
      } else {
        setAuthError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    } catch {
      setAuthError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostRegistration = async (idToken: string, firebaseUser: any) => {
    try {
      await getCustomerValidationNew(idToken);
    } catch {
      const { firstName, lastName } = getValues();
      await customerUpdateNew(idToken, firstName, lastName);
    }
    const { firstName, lastName } = getValues();
    await firebaseUser.updateProfile({
      displayName: `${firstName} ${lastName}`,
    });
    await handleCreateCustomToken(idToken);
  };

  const handleRegister = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    setIsLoading(true);
    try {
      await firebase
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const { email, password } = getValues();
      const result = await firebase
        .auth()
        .createUserWithEmailAndPassword(email, password);
      const firebaseUser = result.user;
      if (firebaseUser && !firebaseUser.emailVerified) {
        await firebaseUser.sendEmailVerification();
      }
      const idToken = await firebaseUser?.getIdToken(true);
      if (idToken) await handlePostRegistration(idToken, firebaseUser);
    } catch (error) {
      setAuthError((error as AxiosError).message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = (
    id: keyof CustomerForm,
    label: string,
    type: string,
    showToggle?: boolean,
    toggleHandler?: () => void,
  ) => (
    <TextfieldDefault
      id={id}
      name={id}
      label={label}
      type={
        showToggle
          ? (id === "password" ? showPassword : showConfirmPassword)
            ? "text"
            : "password"
          : type
      }
      inputRef={register({ required: true, minLength: 2, maxLength: 80 })}
      InputLabelProps={{ style: { color: params.backText } }}
      style={{
        border: `1px solid ${params.buttonColour}`,
        backgroundColor: params.backColour,
      }}
      inputProps={
        showToggle
          ? {
              style: {
                color: params.backText, // <-- Typing text color
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleHandler} edge="end">
                    {(
                      id === "password" ? showPassword : showConfirmPassword
                    ) ? (
                      <Visibility style={{ color: params.backText }} />
                    ) : (
                      <VisibilityOff style={{ color: params.backText }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }
          : {
              style: {
                color: params.backText, // <-- Typing text color
              },
            }
      }
      error={errors[id]?.type}
    />
  );

  return (
    <div
      className={classes.root}
      style={{ backgroundColor: params.backColour }}
    >
      <Box className={classes.headerWrapper}>
        <Box
          className={classes.header}
          style={{ backgroundColor: params.headColour }}
        >
          {params.logo ? (
            <>
              <img
                src={params.logo as string}
                alt="Logo"
                className={classes.logo}
              />
              <Typography variant="body1" style={{ color: params.headText }}>
                Register
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                style={{ color: params.headText, fontWeight: "bold" }}
              >
                {params.title}
              </Typography>
              {params.SubTitle && (
                <Typography
                  variant="subtitle1"
                  style={{ color: params.headText }}
                >
                  {params.SubTitle}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Box className={classes.content}>
          {renderField("email", "Email Address", "text")}
          {errors.email && (
            <ValidationMessage message="Please enter valid email" />
          )}

          {renderField("firstName", "First Name", "text")}
          {errors.firstName && (
            <ValidationMessage message="Please enter valid first name" />
          )}

          {renderField("lastName", "Last Name", "text")}
          {errors.lastName && (
            <ValidationMessage message="Please enter valid last name" />
          )}

          {renderField(
            "password",
            "Password",
            "password",
            true,
            togglePasswordVisibility,
          )}
          {errors.password && (
            <ValidationMessage message="Please enter valid password" />
          )}

          {renderField(
            "confirmPassword",
            "Confirm Password",
            "password",
            true,
            toggleConfirmPasswordVisibility,
          )}
          {errors.confirmPassword && (
            <ValidationMessage message="Please confirm your password" />
          )}

          <Button
            fullWidth
            style={{
              backgroundColor: params.buttonColour,
              color: params.ButtonText,
              height: 50,
              borderRadius: 12,
              marginTop: 8,
            }}
            onClick={handleSubmit(handleRegister)}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "REGISTER"
            )}
          </Button>

          <Box className={classes.registerWrapper}>
            <Typography style={{ color: params.backText }}>
              Already have an account?
            </Typography>
            <Button
              onClick={handleChangeLoginStatus}
              style={{
                textTransform: "none",
                fontWeight: "bold",
                color: params.backText,
              }}
            >
              Login
            </Button>
          </Box>
        </Box>{" "}
      </Box>
    </div>
  );
};

export default RegistrationPage;
